import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase/config';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { Header, Segment, Table, Label, Message, Statistic, Grid, List, Icon } from 'semantic-ui-react';
import { formatDate } from '../../utils/dateUtils';
import { Timestamp } from 'firebase/firestore';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    initiatedName?: string;
    role: 'student' | 'grader';
}

interface Test {
    id: string;
    testName: string;
    type: 'openBook' | 'closeBook' | 'verseMemorization';
    totalGrade: number;
    lastSubmissionDate?: Timestamp | Date;
    criteria?: {
        shortAnswer: { description: string; grade: number };
        longAnswer: { description: string; grade: number };
    };
}

interface Submission {
    studentId: string;
    submittedAt?: Date;
    gradingStatus?: 'pending' | 'graded';
    grade?: number;
    requiresRewrite?: boolean;
    essay?: string;
    criteriaGrades?: Array<{
        description: string;
        grade: number;
        actualGrade: number;
    }>;
    graderComments?: string;
    submitted?: boolean;
    pdfUrl?: string;
    answers?: { shortAnswer?: unknown[]; longAnswer?: unknown[] };
    answer?: string;
    grades?: {
        shortAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
        longAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
    };
}

interface ServiceEntry {
    id: string;
    weekStartDate: Date;
    hours: number;
    supervisor: string;
    description: string;
    submittedBy: string;
    submittedAt: Date;
}

interface FinalResultProps {
    moduleId: string;
}

const FinalResult: React.FC<FinalResultProps> = ({ moduleId }) => {
    const [user] = useAuthState(auth);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tests, setTests] = useState<Test[]>([]);
    const [submissions, setSubmissions] = useState<Record<string, Submission>>({});
    const [serviceEntries, setServiceEntries] = useState<ServiceEntry[]>([]);
    const [finalGrade, setFinalGrade] = useState<number>(0);
    const [moduleResult, setModuleResult] = useState<string>('');
    const [failureReasons, setFailureReasons] = useState<string[]>([]);
    const [allTestsGraded, setAllTestsGraded] = useState<boolean>(false);
    const [pendingGradingMessage, setPendingGradingMessage] = useState<string>('');

    useEffect(() => {
        if (!user || !moduleId) return;
        fetchFinalResultData();
    }, [user, moduleId]);

    const fetchFinalResultData = async () => {
        try {
            setLoading(true);
            
            // Fetch all tests in the module
            const testsSnapshot = await getDocs(collection(db, 'modules', moduleId, 'tests'));
            const testsData: Test[] = testsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Test));
            setTests(testsData);

            // Fetch all submissions for the current user
            const submissionsData: Record<string, Submission> = {};
            
            for (const test of testsData) {
                try {
                    const submissionDoc = await getDoc(
                        doc(db, 'modules', moduleId, 'tests', test.id, 'submissions', user!.uid)
                    );
                    
                    if (submissionDoc.exists()) {
                        const submissionData = submissionDoc.data();
                        
                        if (test.type === 'openBook' && submissionData.answers?.[0]) {
                            // Open Book Test submission
                            submissionsData[test.id] = {
                                studentId: user!.uid,
                                submittedAt: submissionData.answers[0].submittedAt?.toDate(),
                                gradingStatus: submissionData.answers[0].gradingStatus,
                                grade: submissionData.answers[0].grade,
                                requiresRewrite: submissionData.answers[0].requiresRewrite,
                                essay: submissionData.answers[0].essay,
                                criteriaGrades: submissionData.answers[0].criteriaGrades,
                                graderComments: submissionData.answers[0].graderComments,
                                submitted: submissionData.answers[0].submitted,
                            };
                        } else if (test.type === 'closeBook' && (submissionData.answers?.shortAnswer || submissionData.answers?.longAnswer)) {
                            submissionsData[test.id] = {
                                studentId: user!.uid,
                                submittedAt: submissionData.submittedAt?.toDate(),
                                gradingStatus: submissionData.gradingStatus,
                                grade: submissionData.grade,
                                graderComments: submissionData.graderComments,
                                submitted: true,
                                answers: submissionData.answers,
                                grades: submissionData.grades
                            };
                        } else if (test.type === 'verseMemorization' && submissionData.answer !== undefined) {
                            submissionsData[test.id] = {
                                studentId: user!.uid,
                                submittedAt: submissionData.submittedAt?.toDate(),
                                gradingStatus: submissionData.gradingStatus,
                                grade: submissionData.grade,
                                graderComments: submissionData.graderComments,
                                submitted: true,
                                answer: submissionData.answer
                            };
                        } else if ((test.type === 'closeBook' || test.type === 'verseMemorization') && submissionData.pdfUrl) {
                            submissionsData[test.id] = {
                                studentId: user!.uid,
                                submittedAt: submissionData.submittedAt?.toDate(),
                                gradingStatus: submissionData.gradingStatus,
                                grade: submissionData.grade,
                                graderComments: submissionData.graderComments,
                                submitted: true,
                                pdfUrl: submissionData.pdfUrl,
                                grades: submissionData.grades
                            };
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching submission for test ${test.id}:`, err);
                }
            }
            
            setSubmissions(submissionsData);

            // Fetch service log entries
            let serviceEntriesData: ServiceEntry[] = [];
            try {
                const serviceDoc = await getDoc(doc(db, 'modules', moduleId, 'serviceLogs', user!.uid));
                if (serviceDoc.exists()) {
                    const serviceData = serviceDoc.data();
                    const entries = serviceData.submissions || [];
                    serviceEntriesData = entries.map((entry: any) => ({
                        id: entry.id,
                        weekStartDate: entry.weekStartDate.toDate(),
                        hours: entry.hours,
                        supervisor: entry.supervisor,
                        description: entry.description,
                        submittedBy: entry.submittedBy,
                        submittedAt: entry.submittedAt.toDate()
                    }));
                    setServiceEntries(serviceEntriesData);
                }
            } catch (err) {
                console.error('Error fetching service entries:', err);
            }

            // Check if all tests are graded and calculate final grade
            const gradingStatus = checkGradingStatusAndCalculate(testsData, submissionsData, serviceEntriesData);
            setAllTestsGraded(gradingStatus.allGraded);
            setPendingGradingMessage(gradingStatus.message);
            
            if (gradingStatus.allGraded) {
                setFinalGrade(gradingStatus.finalGrade);
                setModuleResult(gradingStatus.moduleResult);
                setFailureReasons(gradingStatus.failureReasons);
            }

        } catch (err) {
            console.error('Error fetching final result data:', err);
            setError('Failed to load final result data');
        } finally {
            setLoading(false);
        }
    };

    const checkGradingStatusAndCalculate = (tests: Test[], submissions: Record<string, Submission>, serviceEntries: ServiceEntry[]) => {
        const openBookTests = tests.filter(test => test.type === 'openBook');
        const closeBookTest = tests.find(test => test.type === 'closeBook');
        const verseTest = tests.find(test => test.type === 'verseMemorization');
        
        let allGraded = true;
        let message = '';
        const missingSubmissions: string[] = [];
        const pendingGrading: string[] = [];

        // Check for missing submissions and pending grading
        const allTests = [...openBookTests];
        if (closeBookTest) allTests.push(closeBookTest);
        if (verseTest) allTests.push(verseTest);

        for (const test of allTests) {
            const submission = submissions[test.id];
            if (!submission || !submission.submitted) {
                missingSubmissions.push(test.testName);
                allGraded = false;
            } else if (!submission.gradingStatus || submission.gradingStatus === 'pending') {
                pendingGrading.push(test.testName);
                allGraded = false;
            }
        }

        // Create detailed message based on what's missing or pending
        if (missingSubmissions.length > 0 && pendingGrading.length > 0) {
            message = `Missing submissions: ${missingSubmissions.join(', ')}. Pending grading: ${pendingGrading.join(', ')}. Please submit missing tests and check back later for grading results.`;
        } else if (missingSubmissions.length > 0) {
            message = `Missing submissions: ${missingSubmissions.join(', ')}. Please submit these tests to view your final results.`;
        } else if (pendingGrading.length > 0) {
            message = `Pending grading: ${pendingGrading.join(', ')}. Please check back later once they have been graded.`;
        }

        // If all tests are graded, calculate the final grade
        if (allGraded) {
            return calculateFinalGrade(tests, submissions, serviceEntries, message);
        }

        return {
            allGraded: false,
            message,
            finalGrade: 0,
            moduleResult: '',
            failureReasons: []
        };
    };

    const calculateFinalGrade = (tests: Test[], submissions: Record<string, Submission>, serviceEntries: ServiceEntry[], message: string = '') => {
        const openBookTests = tests.filter(test => test.type === 'openBook');
        const closeBookTest = tests.find(test => test.type === 'closeBook');
        const verseTest = tests.find(test => test.type === 'verseMemorization');
        
        const failureReasons: string[] = [];
        let finalGrade = 0;
        let moduleResult = 'PASS';

        // Calculate Open Book Tests (40% of total grade)
        let openBookGrade = 0;
        if (openBookTests.length > 0) {
            let totalOpenBookGrade = 0;
            let allOpenBookPassed = true;
            
            for (const test of openBookTests) {
                const submission = submissions[test.id];
                if (!submission || !submission.submitted) {
                    failureReasons.push(`Open Book Test "${test.testName}" not submitted`);
                    allOpenBookPassed = false;
                } else if (submission.requiresRewrite || (submission.grade && submission.grade < 13)) {
                    failureReasons.push(`Open Book Test "${test.testName}" requires rewrite (grade: ${submission.grade}/20)`);
                    allOpenBookPassed = false;
                }
                
                // Always add the grade to total calculation, regardless of rewrite status
                if (submission && submission.grade) {
                    totalOpenBookGrade += submission.grade;
                }
            }
            
            // Always calculate open book grade, regardless of rewrite status
            if (openBookTests.length > 0) {
                openBookGrade = (totalOpenBookGrade / openBookTests.length) * 2; // Convert to percentage (out of 40%)
            }
        }

        // Calculate Close Book Test (30% of total grade - 60% of the 50% close book portion)
        let closeBookGrade = 0;
        if (closeBookTest) {
            const submission = submissions[closeBookTest.id];
            if (!submission || !submission.submitted) {
                failureReasons.push('Close Book Test not submitted');
            } else if (submission.grade !== undefined) {
                const closeBookPercentage = (submission.grade / closeBookTest.totalGrade) * 100;
                if (closeBookPercentage < 65) {
                    failureReasons.push(`Close Book Test grade too low: ${closeBookPercentage.toFixed(1)}% (minimum 65% required)`);
                }
                closeBookGrade = (closeBookPercentage / 100) * 30; // 30% of total grade
            }
        }

        // Calculate Verse Memorization (20% of total grade - 40% of the 50% close book portion)
        let verseGrade = 0;
        if (verseTest) {
            const submission = submissions[verseTest.id];
            if (!submission || !submission.submitted) {
                failureReasons.push('Verse Memorization not submitted');
            } else if (submission.grade !== undefined) {
                const versePercentage = (submission.grade / verseTest.totalGrade) * 100;
                if (versePercentage < 65) {
                    failureReasons.push(`Verse Memorization grade too low: ${versePercentage.toFixed(1)}% (minimum 65% required)`);
                }
                verseGrade = (versePercentage / 100) * 20; // 20% of total grade
            }
        }

        // Calculate Service Hours (10% of total grade)       
        const totalServiceHours = serviceEntries.reduce((total, entry) => total + entry.hours, 0);       
        const serviceGrade = totalServiceHours >= 6 ? 10 : 0;        

        // Calculate final grade
        finalGrade = openBookGrade + closeBookGrade + verseGrade + serviceGrade;

        // Determine module result
        if (failureReasons.length > 0) {
            moduleResult = 'REWRITE MODULE';
        } else if (finalGrade < 70) {
            moduleResult = 'FAIL';
            failureReasons.push(`Final grade too low: ${finalGrade.toFixed(1)}% (minimum 70% required)`);
        }

        return {
            allGraded: true,
            message,
            finalGrade,
            moduleResult,
            failureReasons
        };
    };

    const getTestStatus = (test: Test, submission: Submission | undefined) => {
        if (!submission || !submission.submitted) {
            return <Label color="red">Not Submitted</Label>;
        }
        if (!submission.gradingStatus || submission.gradingStatus === 'pending') {
            return <Label color="yellow">Pending Grading</Label>;
        }
        if (submission.requiresRewrite) {
            return <Label color="red">Rewrite Required</Label>;
        }
        return <Label color="green">Graded</Label>;
    };

    const getTestGrade = (test: Test, submission: Submission | undefined) => {
        if (!submission || !submission.submitted) return 'N/A';
        const grade = submission.grade;
        if (grade === undefined) return 'N/A';
        if (test.type === 'closeBook' && submission.grades && typeof submission.grades.shortAnswer === 'number' && typeof submission.grades.longAnswer === 'number') {
            return `${submission.grades.shortAnswer + submission.grades.longAnswer}/${test.totalGrade}`;
        }
        return `${grade}/${test.totalGrade}`;
    };

    const getTestPercentage = (test: Test, submission: Submission | undefined) => {
        if (!submission || !submission.submitted || submission.grade === undefined) return 'N/A';
        const grade = submission.grade;
        return `${((grade / test.totalGrade) * 100).toFixed(1)}%`;
    };

    if (loading) return <div>Loading final results...</div>;
    if (error) return <Message negative>{error}</Message>;

    const openBookTests = tests.filter(test => test.type === 'openBook');
    const closeBookTest = tests.find(test => test.type === 'closeBook');
    const verseTest = tests.find(test => test.type === 'verseMemorization');
    const totalServiceHours = serviceEntries.reduce((total, entry) => total + entry.hours, 0);

    return (
        <div>
            <Header as="h2">Final Result</Header>
            
            {/* Pending Grading Message */}
            {!allTestsGraded && (
                <Message warning>
                    <Message.Header>Results Pending</Message.Header>
                    <p>{pendingGradingMessage}</p>
                </Message>
            )}
            
            {/* Module Result Summary - Only show if all tests are graded */}
            {allTestsGraded && (
                <Segment color={moduleResult === 'PASS' ? 'green' : 'red'}>
                <Header as="h3">
                    Module Result: {moduleResult}
                    {moduleResult !== 'PASS' && (
                        <Label color="red" style={{ marginLeft: '1rem' }}>
                            {moduleResult}
                        </Label>
                    )}
                </Header>
                
                {failureReasons.length > 0 && (
                    <Message negative>
                        <Message.Header>Reasons for Module Result:</Message.Header>
                        <List bulleted>
                            {failureReasons.map((reason, index) => (
                                <List.Item key={index}>{reason}</List.Item>
                            ))}
                        </List>
                    </Message>
                )}
            </Segment>
            )}

            {/* Grade Breakdown - Only show if all tests are graded */}
            {allTestsGraded && (
            <Grid columns={2} stackable>
                <Grid.Column>
                    <Segment>
                        <Header as="h4">Grade Breakdown</Header>
                        <Statistic.Group size="small">
                            <Statistic>
                                <Statistic.Value>{finalGrade.toFixed(1)}%</Statistic.Value>
                                <Statistic.Label>Final Grade</Statistic.Label>
                            </Statistic>
                            <Statistic>
                                <Statistic.Value>{totalServiceHours}</Statistic.Value>
                                <Statistic.Label>Service Hours</Statistic.Label>
                            </Statistic>
                        </Statistic.Group>
                    </Segment>
                </Grid.Column>
                <Grid.Column>
                    <Segment>
                        <Header as="h4">Grade Calculation Formula</Header>
                        <List>
                            <List.Item>40% - Open Book Tests (average of all 3)</List.Item>
                            <List.Item>30% - Close Book Test (60% of close book portion)</List.Item>
                            <List.Item>20% - Verse Memorization (40% of close book portion)</List.Item>
                            <List.Item>10% - Service Hours (if ≥6 hours)</List.Item>
                        </List>
                    </Segment>
                </Grid.Column>
            </Grid>
            )}

            {/* Open Book Tests - Only show if all tests are graded */}
            {allTestsGraded && openBookTests.length > 0 && (
                <Segment>
                    <Header as="h3">Open Book Tests (40% of total grade)</Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Test Name</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                                <Table.HeaderCell>Grade</Table.HeaderCell>
                                <Table.HeaderCell>Percentage</Table.HeaderCell>
                                <Table.HeaderCell>Comments</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {openBookTests.map(test => {
                                const submission = submissions[test.id];
                                return (
                                    <Table.Row key={test.id}>
                                        <Table.Cell>{test.testName}</Table.Cell>
                                        <Table.Cell>{getTestStatus(test, submission)}</Table.Cell>
                                        <Table.Cell>{getTestGrade(test, submission)}</Table.Cell>
                                        <Table.Cell>{getTestPercentage(test, submission)}</Table.Cell>
                                        <Table.Cell>
                                            {submission?.graderComments || 'No comments'}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            {/* Close Book Test - Only show if all tests are graded */}
            {allTestsGraded && closeBookTest && (
                <Segment>
                    <Header as="h3">Close Book Test (30% of total grade)</Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Test Name</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                                <Table.HeaderCell>Grade</Table.HeaderCell>
                                <Table.HeaderCell>Percentage</Table.HeaderCell>
                                <Table.HeaderCell>Comments</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>{closeBookTest.testName}</Table.Cell>
                                <Table.Cell>{getTestStatus(closeBookTest, submissions[closeBookTest.id])}</Table.Cell>
                                <Table.Cell>{getTestGrade(closeBookTest, submissions[closeBookTest.id])}</Table.Cell>
                                <Table.Cell>{getTestPercentage(closeBookTest, submissions[closeBookTest.id])}</Table.Cell>
                                <Table.Cell>
                                    {submissions[closeBookTest.id]?.graderComments || 'No comments'}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            {/* Verse Memorization - Only show if all tests are graded */}
            {allTestsGraded && verseTest && (
                <Segment>
                    <Header as="h3">Verse Memorization (20% of total grade)</Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Test Name</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                                <Table.HeaderCell>Grade</Table.HeaderCell>
                                <Table.HeaderCell>Percentage</Table.HeaderCell>
                                <Table.HeaderCell>Comments</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>{verseTest.testName}</Table.Cell>
                                <Table.Cell>{getTestStatus(verseTest, submissions[verseTest.id])}</Table.Cell>
                                <Table.Cell>{getTestGrade(verseTest, submissions[verseTest.id])}</Table.Cell>
                                <Table.Cell>{getTestPercentage(verseTest, submissions[verseTest.id])}</Table.Cell>
                                <Table.Cell>
                                    {submissions[verseTest.id]?.graderComments || 'No comments'}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            {/* Service Hours - Only show if all tests are graded */}
            {allTestsGraded && (
            <Segment>
                <Header as="h3">Service Hours (10% of total grade)</Header>
                <Table celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Total Hours</Table.HeaderCell>
                            <Table.HeaderCell>Contribution to Grade</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>{totalServiceHours}</Table.Cell>
                            <Table.Cell>{totalServiceHours >= 6 ? '10%' : '0%'}</Table.Cell>
                            <Table.Cell>
                                <Label color={totalServiceHours >= 6 ? 'green' : 'yellow'}>
                                    {totalServiceHours >= 6 ? 'Sufficient' : 'Insufficient (minimum 6 hours required)'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </Segment>
            )}
        </div>
    );
};

export default FinalResult;
